// src/pages/partner-admin/PartnerSubmissionsPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  getPartnerCompetitions,
  getPartnerSubmissions,
  approveSubmission,
  rejectSubmission,
} from "@/services/partnerAdminService";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function Avatar({ user }) {
  const url = user?.profile?.avatar_url;
  const initials = (user?.username || "?")[0].toUpperCase();
  return url ? (
    <img src={url} alt={user.username} className="pa-avatar" />
  ) : (
    <div className="pa-avatar pa-avatar--initials">{initials}</div>
  );
}

// ─── Submission detail panel ──────────────────────────────────────────────────

function SubmissionPanel({ submission, onClose, onApprove, onReject, acting }) {
  if (!submission) return null;

  const files = submission.files || [];
  const user  = submission.user || {};

  return (
    <div className="pa-panel-backdrop" onClick={onClose}>
      <div className="pa-panel" onClick={e => e.stopPropagation()}>
        <div className="pa-panel__header">
          <div className="pa-panel__user">
            <Avatar user={user} />
            <div>
              <div className="pa-panel__username">{user.username || "—"}</div>
              <div className="pa-panel__meta">{fmt(submission.submission_date)}</div>
            </div>
          </div>
          <button className="pa-modal__close" onClick={onClose}>✕</button>
        </div>

        {/* Files */}
        <div className="pa-panel__files">
          {files.length === 0 && <div className="pa-panel__empty">No files attached.</div>}
          {files.map((f, i) => (
            <div key={i} className="pa-panel__file">
              {f.file_type === "image" ? (
                <img src={f.file_url} alt="submission" className="pa-panel__img" />
              ) : (
                <video src={f.file_url} controls className="pa-panel__video" />
              )}
            </div>
          ))}
        </div>

        {/* Description */}
        {submission.description && (
          <div className="pa-panel__section">
            <div className="pa-panel__label">Description</div>
            <div className="pa-panel__text">{submission.description}</div>
          </div>
        )}

        {/* Stats row */}
        <div className="pa-panel__stats">
          <div className="pa-panel__stat">
            <span className="pa-panel__stat-val">{submission.votes_count ?? 0}</span>
            <span className="pa-panel__stat-key">Votes</span>
          </div>
          <div className="pa-panel__stat">
            <span
              className="pa-panel__stat-val"
              style={{
                color: submission.status === 'approved' ? "#c8f04a" :
                       submission.status === 'rejected' ? "#f04a4a" : "#f0a84a"
              }}
            >
              {submission.status === 'approved' ? "Approved" :
               submission.status === 'rejected' ? "Rejected" : "Pending"}
            </span>
            <span className="pa-panel__stat-key">Status</span>
          </div>
        </div>

        {/* Actions */}
        <div className="pa-panel__actions">
          {submission.status === 'approved' ? (
            <button
              className="pa-btn pa-btn--reject"
              onClick={() => onReject(submission.id)}
              disabled={acting}
            >
              {acting ? "Rejecting..." : "✕ Reject"}
            </button>
          ) : submission.status === 'rejected' ? (
            <button
              className="pa-btn pa-btn--approve"
              onClick={() => onApprove(submission.id)}
              disabled={acting}
            >
              {acting ? "Approving..." : "✓ Approve"}
            </button>
          ) : (
            <>
              <button
                className="pa-btn pa-btn--approve"
                onClick={() => onApprove(submission.id)}
                disabled={acting}
              >
                {acting ? "Approving..." : "✓ Approve"}
              </button>
              <button
                className="pa-btn pa-btn--reject"
                onClick={() => onReject(submission.id)}
                disabled={acting}
              >
                {acting ? "Rejecting..." : "✕ Reject"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function SubmissionRow({ sub, onSelect, onApprove, onReject, acting }) {
  const user  = sub.user  || {};
  const thumb = sub.files?.[0];

  return (
    <tr className="pa-table__row" onClick={() => onSelect(sub)}>
      {/* Thumbnail */}
      <td style={{ width: 56 }}>
        {thumb?.file_type === "image" ? (
          <img src={thumb.file_url} alt="" className="pa-sub-thumb" />
        ) : thumb?.file_type === "video" ? (
          <div className="pa-sub-thumb pa-sub-thumb--video">▶</div>
        ) : (
          <div className="pa-sub-thumb pa-sub-thumb--empty">—</div>
        )}
      </td>

      {/* User */}
      <td>
        <div className="pa-sub-user">
          <Avatar user={user} />
          <span className="pa-table__title">{user.username || "—"}</span>
        </div>
      </td>

      {/* Description */}
      <td className="pa-sub-desc">
        <span>{sub.description || <em style={{ color: "#444" }}>No description</em>}</span>
      </td>

      {/* Votes */}
      <td className="pa-table__num">{sub.votes_count ?? 0}</td>

      {/* Date */}
      <td className="pa-table__date">{fmt(sub.submission_date)}</td>

      {/* Status + actions */}
      <td onClick={e => e.stopPropagation()}>
        <div className="pa-sub-actions">
          {sub.status === 'approved' ? (
            <>
              <span className="pa-badge pa-badge--approved">Approved</span>
              <button
                className="pa-act-btn pa-act-btn--reject"
                onClick={() => onReject(sub.id)}
                disabled={acting === sub.id}
                title="Reject"
              >✕</button>
            </>
          ) : sub.status === 'rejected' ? (
            <>
              <span className="pa-badge pa-badge--rejected">Rejected</span>
              <button
                className="pa-act-btn pa-act-btn--approve"
                onClick={() => onApprove(sub.id)}
                disabled={acting === sub.id}
                title="Approve"
              >✓</button>
            </>
          ) : (
            <>
              <span className="pa-badge pa-badge--pending">Pending</span>
              <button
                className="pa-act-btn pa-act-btn--approve"
                onClick={() => onApprove(sub.id)}
                disabled={acting === sub.id}
                title="Approve"
              >✓</button>
              <button
                className="pa-act-btn pa-act-btn--reject"
                onClick={() => onReject(sub.id)}
                disabled={acting === sub.id}
                title="Reject"
              >✕</button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PartnerSubmissionsPage() {
  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState("");
  const [submissions, setSubmissions]   = useState([]);
  const [filter, setFilter]             = useState("all"); // all | pending | approved | rejected
  const [loading, setLoading]           = useState(false);
  const [compsLoading, setCompsLoading] = useState(true);
  const [error, setError]               = useState(null);
  const [selected, setSelected]         = useState(null); // open panel
  const [acting, setActing]             = useState(null); // submission id being actioned

  // Load competitions for the dropdown
  useEffect(() => {
    getPartnerCompetitions()
      .then(data => {
        setCompetitions(data);
      })
      .catch(err => setError(err.message))
      .finally(() => setCompsLoading(false));
  }, []);

  // Load submissions when comp or filter changes
  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedComp) params.competition = selectedComp;
      if (filter === "pending")  params.status = "pending";
      if (filter === "approved") params.status = "approved";
      if (filter === "rejected") params.status = "rejected";
      const data = await getPartnerSubmissions(params);
      setSubmissions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedComp, filter]);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const handleApprove = async (id) => {
    setActing(id);
    try {
      await approveSubmission(id);
      await loadSubmissions();
      if (selected?.id === id) setSelected(prev => ({ ...prev, status: 'approved' }));
    } catch (err) {
      alert(err.message);
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (id) => {
    setActing(id);
    try {
      await rejectSubmission(id);
      await loadSubmissions();
      if (selected) setSelected(prev => ({ ...prev, status: 'rejected' }));
    } catch (err) {
      alert(err.message);
    } finally {
      setActing(null);
    }
  };

  const pendingCount  = submissions.filter(s => s.status === 'pending').length;
  const approvedCount = submissions.filter(s => s.status === 'approved').length;
  const rejectedCount = submissions.filter(s => s.status === 'rejected').length;

  const visibleSubs = submissions.filter(s => {
    if (filter === "pending")  return s.status === 'pending';
    if (filter === "approved") return s.status === 'approved';
    if (filter === "rejected") return s.status === 'rejected';
    return true;
  });

  return (
    <div className="pa-submissions">

      {/* Top controls */}
      <div className="pa-toolbar">
        <div className="pa-toolbar__left">
          {/* Competition picker */}
          <select
            className="pa-comp-select"
            value={selectedComp}
            onChange={e => setSelectedComp(e.target.value)}
            disabled={compsLoading}
          >
            {compsLoading && <option>Loading...</option>}
            <option value="">All competitions</option>
            {competitions.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>

          {/* Filter tabs */}
          <div className="pa-filter-tabs">
            {[
              { key: "all",      label: `All (${submissions.length})` },
              { key: "pending",  label: `Pending (${pendingCount})`,   warn: pendingCount > 0 },
              { key: "approved", label: `Approved (${approvedCount})` },
              { key: "rejected", label: `Rejected (${rejectedCount})` },
            ].map(({ key, label, warn }) => (
              <button
                key={key}
                className={"pa-filter-tab" + (filter === key ? " pa-filter-tab--active" : "") + (warn ? " pa-filter-tab--warn" : "")}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="pa-page-loading">Loading submissions...</div>
      ) : error ? (
        <div className="pa-page-error">{error}</div>
      ) : visibleSubs.length === 0 ? (
        <div className="pa-empty">
          {filter === "pending" ? "No pending submissions." : filter === "approved" ? "No approved submissions." : filter === "rejected" ? "No rejected submissions." : "No submissions yet."}
        </div>
      ) : (
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr>
                <th></th>
                <th>User</th>
                <th>Description</th>
                <th>Votes</th>
                <th>Submitted</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleSubs.map(sub => (
                <SubmissionRow
                  key={sub.id}
                  sub={sub}
                  onSelect={setSelected}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  acting={acting}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <SubmissionPanel
          submission={selected}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          acting={acting === selected.id ? selected.status : null}
        />
      )}

      <style>{`
        .pa-submissions { display: flex; flex-direction: column; gap: 20px; }

        /* Toolbar */
        .pa-toolbar {
          display: flex; align-items: center;
          justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .pa-toolbar__left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        .pa-comp-select {
          background: #111; border: 1px solid #2a2a2a; color: #ccc;
          padding: 8px 12px; border-radius: 6px; font-size: 13px;
          outline: none; cursor: pointer; min-width: 200px;
        }
        .pa-comp-select:focus { border-color: var(--colour-primary, #c8f04a); }

        .pa-filter-tabs { display: flex; gap: 4px; }
        .pa-filter-tab {
          background: none; border: 1px solid #1e1e1e; color: #555;
          font-size: 11px; padding: 5px 10px; border-radius: 4px;
          cursor: pointer; transition: border-color 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .pa-filter-tab:hover { border-color: #333; color: #aaa; }
        .pa-filter-tab--active {
          border-color: var(--colour-primary, #c8f04a);
          color: var(--colour-primary, #c8f04a);
        }
        .pa-filter-tab--warn { color: #f0a84a; border-color: #3a2a1a; }
        .pa-filter-tab--warn.pa-filter-tab--active { border-color: #f0a84a; }

        /* Table */
        .pa-table-wrap {
          background: #111; border: 1px solid #1e1e1e;
          border-radius: 8px; overflow: hidden;
        }
        .pa-table { width: 100%; border-collapse: collapse; }
        .pa-table thead tr { border-bottom: 1px solid #1e1e1e; }
        .pa-table th {
          text-align: left; padding: 10px 16px;
          font-size: 10px; color: #444;
          text-transform: uppercase; letter-spacing: 0.07em; font-weight: 600;
        }
        .pa-table__row {
          border-bottom: 1px solid #161616;
          cursor: pointer; transition: background 0.1s;
        }
        .pa-table__row:last-child { border-bottom: none; }
        .pa-table__row:hover { background: #161616; }
        .pa-table td { padding: 10px 16px; vertical-align: middle; }
        .pa-table__title { font-size: 13px; color: #ccc; font-weight: 500; }
        .pa-table__num   { font-size: 13px; color: #666; text-align: center; }
        .pa-table__date  { font-size: 11px; color: #555; white-space: nowrap; }

        /* Avatar */
        .pa-avatar {
          width: 28px; height: 28px; border-radius: 50%;
          object-fit: cover; flex-shrink: 0;
        }
        .pa-avatar--initials {
          background: #2a2a2a; color: #888; font-size: 11px;
          font-weight: 600; display: flex; align-items: center;
          justify-content: center;
        }

        .pa-sub-thumb {
          width: 40px; height: 40px; border-radius: 4px;
          object-fit: cover; display: block;
        }
        .pa-sub-thumb--video {
          background: #1a1a1a; color: #555; font-size: 14px;
          display: flex; align-items: center; justify-content: center;
        }
        .pa-sub-thumb--empty {
          background: #1a1a1a; color: #333; font-size: 11px;
          display: flex; align-items: center; justify-content: center;
        }

        .pa-sub-user {
          display: flex; align-items: center; gap: 8px;
        }
        .pa-sub-desc {
          font-size: 12px; color: #666; max-width: 280px;
        }
        .pa-sub-desc span {
          display: -webkit-box; -webkit-line-clamp: 2;
          -webkit-box-orient: vertical; overflow: hidden;
        }

        /* Badges */
        .pa-badge {
          font-size: 10px; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.07em; padding: 3px 7px; border-radius: 3px;
        }
        .pa-badge--approved { background: #1a2a0d; color: #c8f04a; border: 1px solid #2a3a1a; }
        .pa-badge--pending  { background: #2a1f0d; color: #f0a84a; border: 1px solid #3a2a1a; }
        .pa-badge--rejected { background: #2a1111; color: #f04a4a; border: 1px solid #4a2222; }

        .pa-sub-actions { display: flex; align-items: center; gap: 6px; }
        .pa-act-btn {
          width: 24px; height: 24px; border-radius: 4px; border: none;
          cursor: pointer; font-size: 12px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.1s; flex-shrink: 0;
        }
        .pa-act-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .pa-act-btn--approve { background: #1a2a0d; color: #c8f04a; }
        .pa-act-btn--approve:hover:not(:disabled) { background: #2a3a1a; }
        .pa-act-btn--reject  { background: #2a1111; color: #f04a4a; }
        .pa-act-btn--reject:hover:not(:disabled)  { background: #3a1a1a; }

        /* Detail panel */
        .pa-panel-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.6);
          display: flex; align-items: center; justify-content: flex-end;
          z-index: 1000;
        }
        .pa-panel {
          background: #141414; border-left: 1px solid #222;
          width: 420px; max-width: 100vw; height: 100vh;
          overflow-y: auto; display: flex; flex-direction: column;
          animation: slideIn 0.2s ease;
        }
        @keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

        .pa-panel__header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px; border-bottom: 1px solid #1e1e1e;
          position: sticky; top: 0; background: #141414; z-index: 1;
        }
        .pa-panel__user { display: flex; align-items: center; gap: 10px; }
        .pa-panel__username { font-size: 14px; font-weight: 600; color: #e8e8e8; }
        .pa-panel__meta { font-size: 11px; color: #555; margin-top: 2px; }

        .pa-panel__files {
          padding: 16px; display: flex; flex-direction: column; gap: 10px;
        }
        .pa-panel__img {
          width: 100%; border-radius: 6px; object-fit: cover;
          max-height: 320px; display: block;
        }
        .pa-panel__video {
          width: 100%; border-radius: 6px; max-height: 320px;
        }
        .pa-panel__empty { color: #444; font-size: 13px; padding: 12px 0; }

        .pa-panel__section { padding: 0 16px 16px; }
        .pa-panel__label {
          font-size: 10px; color: #444; text-transform: uppercase;
          letter-spacing: 0.07em; margin-bottom: 6px;
        }
        .pa-panel__text { font-size: 13px; color: #888; line-height: 1.5; }

        .pa-panel__stats {
          display: flex; gap: 0; border-top: 1px solid #1e1e1e;
          border-bottom: 1px solid #1e1e1e; margin: 0;
        }
        .pa-panel__stat {
          flex: 1; padding: 14px 16px; display: flex;
          flex-direction: column; gap: 3px;
          border-right: 1px solid #1e1e1e;
        }
        .pa-panel__stat:last-child { border-right: none; }
        .pa-panel__stat-val { font-size: 18px; font-weight: 700; color: #e8e8e8; }
        .pa-panel__stat-key { font-size: 10px; color: #444; text-transform: uppercase; letter-spacing: 0.07em; }

        .pa-panel__actions {
          padding: 16px; display: flex; gap: 10px;
          position: sticky; bottom: 0; background: #141414;
          border-top: 1px solid #1e1e1e;
        }
        .pa-btn {
          flex: 1; padding: 10px; border-radius: 6px; font-size: 13px;
          font-weight: 600; cursor: pointer; border: none;
          transition: opacity 0.15s;
        }
        .pa-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pa-btn--approve { background: #c8f04a; color: #0d0d0d; }
        .pa-btn--approve:hover:not(:disabled) { opacity: 0.9; }
        .pa-btn--reject  { background: #2a1111; color: #f04a4a; border: 1px solid #4a2222; }
        .pa-btn--reject:hover:not(:disabled)  { background: #3a1a1a; }

        .pa-modal__close {
          background: none; border: none; color: #555;
          font-size: 14px; cursor: pointer; padding: 4px 8px;
        }
        .pa-modal__close:hover { color: #aaa; }

        .pa-empty {
          background: #111; border: 1px dashed #222; border-radius: 8px;
          padding: 40px; display: flex; align-items: center;
          justify-content: center; color: #444; font-size: 13px;
        }
        .pa-page-loading { color: #555; font-size: 13px; padding: 40px 0; }
        .pa-page-error   { color: #f04a4a; font-size: 13px; padding: 40px 0; }
      `}</style>
    </div>
  );
}